/**
 * La pantalla donde un desconocido crea su cuenta.
 *
 * **Es pública, y eso es lo que no hay que romper.** Si alguien la pone detrás
 * de `RequireAuth` por simetría con el resto del panel, vuelve el problema que
 * la trajo: `POST /alta` estaba desplegado desde el 2026-09-01 y ninguna
 * pantalla lo llamaba, así que quien entraba a configurar rebotaba al login y
 * no tenía cómo conseguir una cuenta.
 *
 * Lo que sigue después no es esta pantalla: el dueño confirma su correo, entra,
 * y `PaginaDelAlta` lo lleva por la configuración.
 */

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Input } from '../../components/ui/index.ts';
import { api } from '../../lib/api.ts';
import { PasoDeCodigo } from './PasoDeCodigo';
import { textos } from './textos';

const LARGO_MINIMO = 8;

export function PaginaDeRegistro() {
  const [nombreDelNegocio, setNegocio] = useState('');
  const [correo, setCorreo] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);

  /**
   * No hay guarda `if (creando) return` acá.
   *
   * La tuvo, y ninguna mutación podía matarla: React aplica `disabled` antes de
   * que llegue el segundo clic, así que era código muerto fingiendo protección
   * —el mismo caso que `PieDelAlta` el 2026-09-02—. La protección real es el
   * `loading` del botón, y la mutación que lo quita SÍ tumba el test.
   */
  async function enviar(e: FormEvent) {
    e.preventDefault();

    // El servidor la rechaza igual, pero decírselo acá evita un viaje y un
    // error genérico donde el dueño no sabe cuál de los tres campos falló.
    if (contrasena.length < LARGO_MINIMO) {
      setError(textos.registro.claveCorta);
      return;
    }

    setCreando(true);
    setError(null);
    try {
      await api.alta.registrarse({ nombreDelNegocio, correo, contrasena });
      setListo(true);
    } catch {
      setError(textos.registro.noSePudo);
    } finally {
      setCreando(false);
    }
  }

  if (listo) {
    return (
      <main className="alta-pagina">
        <Card>
          <CardHeader>
            <h1 className="card-title">{textos.registro.listoTitulo}</h1>
          </CardHeader>
          <CardBody>
            {/* El código se pega acá mismo. Mandarlo a «entrar después» era el
                punto exacto donde el alta se cortaba: no había dónde ponerlo. */}
            <PasoDeCodigo
              correo={correo}
              onConfirmado={(tenantId) => {
                window.location.href = `/alta/${tenantId}`;
              }}
            />
          </CardBody>
        </Card>
      </main>
    );
  }

  return (
    <main className="alta-pagina">
      <Card>
        <CardHeader>
          {/* `card-title` y no un `h1` pelado: un encabezado sin clase hereda el
              color del body, y hasta hoy ése era oscuro también en modo oscuro
              — o sea invisible sobre la tarjeta. */}
          <h1 className="card-title">{textos.registro.titulo}</h1>
          <p className="card-subtitle">{textos.registro.bajada}</p>
        </CardHeader>
        <CardBody>
          <form onSubmit={enviar} className="alta-registro-form">
            <Input
              label={textos.registro.negocio}
              value={nombreDelNegocio}
              onChange={(e) => setNegocio(e.target.value)}
              required
            />
            <Input
              label={textos.registro.correo}
              type="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              required
            />
            <Input
              label={textos.registro.contrasena}
              type="password"
              helper={textos.registro.ayudaContrasena}
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              required
            />

            {error && (
              <div className="alta-pie-error" role="alert">
                <p>{error}</p>
              </div>
            )}

            <Button type="submit" variant="primary" loading={creando}>
              {creando ? textos.registro.creando : textos.registro.crear}
            </Button>

            <Link to="/login">{textos.registro.yaTengoCuenta}</Link>
          </form>
        </CardBody>
      </Card>
    </main>
  );
}
